import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './api-error.js';
import { sortQuerySchema } from './sort.js';

/**
 * Roles, ordered least to most privileged. Middleware compares by index, so the
 * order of this array is the privilege ladder.
 *
 * @since 1.0.0
 */
export const ROLES = ['viewer', 'dev', 'admin'] as const;

export const roleSchema = z.enum(ROLES);

export type Role = z.infer<typeof roleSchema>;

/**
 * Returns true when a role meets or exceeds the required role.
 *
 * @since 1.0.0
 * @param role - the role held by the session user
 * @param required - the minimum role the route demands
 * @returns whether access is allowed
 */
export function hasRole(role: Role, required: Role): boolean {
    return ROLES.indexOf(role) >= ROLES.indexOf(required);
}

export const loginRequestSchema = z.object({
    email: z.email().max(320),
    password: z.string().min(1).max(512),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const sessionUserSchema = z.object({
    id: z.number().int(),
    email: z.email(),
    name: z.string(),
    role: roleSchema,
});

export type SessionUser = z.infer<typeof sessionUserSchema>;

/**
 * Shortest password a dashboard user may set.
 *
 * @since 1.3.0
 */
export const MIN_PASSWORD_LENGTH = 12;

/**
 * Longest password a dashboard user may set, which also caps the work one hash request costs.
 *
 * @since 1.4.0
 */
export const MAX_PASSWORD_LENGTH = 512;

/**
 * A password a dashboard user may set.
 *
 * @since 1.4.0
 */
export const newPasswordSchema = z.string().min(MIN_PASSWORD_LENGTH).max(MAX_PASSWORD_LENGTH);

/**
 * How long a password setup link stays usable after it is created.
 *
 * @since 1.4.0
 */
export const PASSWORD_LINK_TTL_SECONDS = 7 * 24 * 60 * 60;

/**
 * The dashboard path a password setup link opens. The token travels in the fragment so it
 * never reaches a server log or a referrer header.
 *
 * @since 1.4.0
 */
export const PASSWORD_LINK_PATH = '/set-password';

/**
 * What a visitor reads when their setup link has expired, was already used or never existed.
 *
 * @since 1.4.0
 */
export const PASSWORD_LINK_GONE_MESSAGE = 'This link has expired or was already used. Ask an admin for a new one.';

/**
 * Where an account stands: able to sign in, waiting on its first password, or switched off.
 *
 * @since 1.4.0
 */
export const USER_STATUSES = ['active', 'invited', 'disabled'] as const;

export const userStatusSchema = z.enum(USER_STATUSES);

export type UserStatus = z.infer<typeof userStatusSchema>;

export const createUserRequestSchema = z.object({
    email: z.email().max(320),
    name: z.string().min(1).max(120),
    role: roleSchema,
});

export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;

export const updateUserRequestSchema = z
    .object({
        name: z.string().min(1).max(120).optional(),
        role: roleSchema.optional(),
        password: newPasswordSchema.optional(),
        isDisabled: z.boolean().optional(),
    })
    .refine((value) => Object.keys(value).length > 0, { message: 'No fields to update' });

export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

export const userSchema = sessionUserSchema.extend({
    createdAt: z.number().int(),
    disabledAt: z.number().int().nullable(),
    status: userStatusSchema,
});

export type User = z.infer<typeof userSchema>;

/**
 * A freshly issued password setup link and the moment it stops working.
 *
 * @since 1.4.0
 */
export const passwordLinkResponseSchema = z.object({
    setupLink: z.url(),
    expiresAt: z.number().int(),
});

export type PasswordLinkResponse = z.infer<typeof passwordLinkResponseSchema>;

/**
 * What creating a user returns: the invited account and the link that lets them set a password.
 *
 * @since 1.4.0
 */
export const createUserResponseSchema = passwordLinkResponseSchema.extend({
    user: userSchema,
});

export type CreateUserResponse = z.infer<typeof createUserResponseSchema>;

/**
 * The opaque token carried by a password setup link.
 *
 * @since 1.4.0
 */
export const passwordLinkTokenSchema = z.string().min(1).max(256);

export const checkPasswordLinkRequestSchema = z.object({
    token: passwordLinkTokenSchema,
});

export type CheckPasswordLinkRequest = z.infer<typeof checkPasswordLinkRequestSchema>;

/**
 * Who a still-valid password setup link belongs to, so the page can say whose password is being set.
 *
 * @since 1.4.0
 */
export const checkPasswordLinkResponseSchema = z.object({
    email: z.email(),
    name: z.string(),
});

export type CheckPasswordLinkResponse = z.infer<typeof checkPasswordLinkResponseSchema>;

export const setPasswordRequestSchema = z.object({
    token: passwordLinkTokenSchema,
    password: newPasswordSchema,
});

export type SetPasswordRequest = z.infer<typeof setPasswordRequestSchema>;

/**
 * How the auth routes wrap the signed-in user.
 *
 * @since 1.3.0
 */
export const sessionUserEnvelopeSchema = z.object({
    user: sessionUserSchema,
});

export type SessionUserEnvelope = z.infer<typeof sessionUserEnvelopeSchema>;

/**
 * How the user routes wrap a created or updated user.
 *
 * @since 1.3.0
 */
export const userEnvelopeSchema = z.object({
    user: userSchema,
});

export type UserEnvelope = z.infer<typeof userEnvelopeSchema>;

/**
 * One page of the user roster.
 *
 * @since 1.3.0
 */
export const usersPageSchema = paginatedSchema(userSchema);

export type UsersPage = z.infer<typeof usersPageSchema>;

/**
 * Columns the user roster may be sorted on, named after the table's own column keys.
 *
 * @since 1.4.0
 */
export const USER_SORT_KEYS = ['name', 'email', 'role', 'added', 'status'] as const;

export const userSortKeySchema = z.enum(USER_SORT_KEYS);

export type UserSortKey = z.infer<typeof userSortKeySchema>;

export const userQuerySchema = paginationQuerySchema.extend(sortQuerySchema(USER_SORT_KEYS, 'name', 'asc').shape);

export type UserQuery = z.infer<typeof userQuerySchema>;
